import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  SubtenantDto,
  ResourceDto,
  ResourceType,
  VisitListDto,
} from '@lmt/shared-types';
import { DragObject } from '@lmt/unicorn-shared/app/components/calendar/devextreme/common';
import { AppointmentPopup } from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/AppointmentPopup';
import { VisitContent } from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/VisitContent';
import { AppPopper } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppPopper';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { bindPopper } from 'material-ui-popup-state';
import React, { FC, useCallback, useMemo, useState } from 'react';

import styles from '@lmt/unicorn-shared/app/components/calendar/waiting-list/WaitingListVisitItem.module.scss';
import { bindHover, usePopupState } from 'material-ui-popup-state/hooks';

interface Props {
  visit: VisitListDto;
  subtenant: SubtenantDto;
  color?: string;
  resources: ResourceDto[];
}

export const WaitingListVisitItem: FC<Props> = (props) => {
  const { visit, color, subtenant, resources } = props;
  const forms = useGlobalForms();

  const room = useMemo(() => {
    if (!visit.resourceIds?.length || !resources?.length) {
      return null;
    }
    return resources.find(
      (x) =>
        x.type === ResourceType.Places && visit.resourceIds?.includes(x.id),
    );
  }, [visit, resources]);

  const specialConditions = useMemo(() => {
    if (!visit.resourceIds?.length || !resources?.length) {
      return null;
    }
    return resources.filter(
      (x) =>
        x.type === ResourceType.SpecialConditions &&
        visit.resourceIds?.includes(x.id),
    );
  }, [visit, resources]);

  const doctor = useMemo(() => {
    if (!visit.resourceIds?.length || !resources?.length) {
      return null;
    }
    return resources.find(
      (x) =>
        x.type === ResourceType.Doctors && visit.resourceIds?.includes(x.id),
    );
  }, [visit, resources]);

  const visitData = useMemo(() => {
    return {
      title: visit.title,
      patient: {
        id: visit.patientId,
        fullName: visit.patientName,
      },
      color: color!,
      lineStyles: [],
      subtenantCode: subtenant?.code,
      priority: visit.priority,
      id: visit.id,
      priorityContact: visit.priorityContact,
    };
  }, [visit, color, subtenant]);

  const dragItem: DragObject = useMemo(() => {
    return {
      title: '',
      type: 'visit',
      visitId: visit.id,
      visitData,
    };
  }, [visitData, visit]);

  const sortable = useMemo(
    () => ({ id: `visit_${visit.id}`, data: dragItem }),
    [visit],
  );
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable(sortable);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
  };
  const sortableProps = {
    ref: setNodeRef,
    style,
    ...attributes,
    ...listeners,
  };

  const doubleClickHandler = useCallback(() => {
    forms.openVisitEditing(props.visit.id);
  }, [props.visit.id]);

  const popper = usePopupState({
    variant: 'popper',
    popupId: 'waiting-list-visit-item',
  });
  return (
    <div
      {...sortableProps}
      className={styles.appointmentItem}
      key={visit.id}
      {...bindHover(popper)}
      onDoubleClick={doubleClickHandler}
    >
      <VisitContent data={visitData} />
      <AppPopper {...bindPopper(popper)} delay={500}>
        <AppointmentPopup
          appointmentData={visit}
          patient={{
            id: visit.patientId,
            fullName: visit.patientName,
          }}
          doctor={doctor}
          room={room}
          resources={specialConditions}
          subtenantCode={subtenant?.code}
          priorityContact={visit.priorityContact}
          onClose={popper.close}
        />
      </AppPopper>
    </div>
  );
};
